import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Typography, Box, Grid, Button, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import Loader from '../../../components/loader';
import EkScoreCommercialTerms from './ekScoreCommercialTerms';
import { ecosytemIntegatorGetById, getIntegratorInitiatives, getIntegratorSubUsers } from '../ecosystemPartners.service';
import {
  detailFieldSx,
  detailLabelSx,
  detailPageCardSx as pageCardSx,
  detailSectionTitleSx,
  detailValueSx,
} from '../../../styles/detailStyles';

const subUserHeadCellSx = {
  fontSize: 13,
  fontWeight: 600,
  color: '#625B71',
  py: 1.25,
  whiteSpace: 'nowrap',
};

const subUserCellSx = {
  fontSize: 14,
  color: '#1C1B1F',
  py: 1,
};

const toSentenceCase = (value) => {
  if (!value) return '-';
  return value
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, char => char.toUpperCase());
};

function DetailField({ label, value, fullWidth = false, minHeight, transformValue }) {
  const displayValue = transformValue && typeof value === 'string' ? transformValue(value) : value;
  const isReactElement = displayValue && typeof displayValue === 'object' && displayValue.$$typeof;

  return (
    <Grid size={{ xs: 12, md: fullWidth ? 12 : 6 }}>
      <Box sx={{ ...detailFieldSx, minHeight: minHeight || detailFieldSx.minHeight }}>
        <Typography sx={detailLabelSx}>{label}</Typography>
        {isReactElement ? (
          <Box sx={detailValueSx}>{displayValue}</Box>
        ) : (
          <Typography sx={detailValueSx}>{displayValue || '-'}</Typography>
        )}
      </Box>
    </Grid>
  );
}

// "Initiative Based" (display name) or "INITIATIVE_BASED" (code) -> "INITIATIVE_BASED".
const integratorTypeCode = (rawType = '') =>
  rawType.includes(' ') ? rawType.toUpperCase().replace(/\s+/g, '_') : rawType;

const isEkScoreInitiative = (item) => (item.name || '').replace(/\s+/g, '').toLowerCase() === 'ekscore';

/**
 * Whether the integrator's Initiative membership counts. Newer backends send `membershipStatus`
 * (APPROVED, PENDING, ... or null for an Initiative only picked at sign-up); only APPROVED counts
 * then. Without the field (older backends) every listed Initiative counts, as before.
 */
const isApprovedMembership = (item) =>
  !Object.prototype.hasOwnProperty.call(item, 'membershipStatus') || item.membershipStatus === 'APPROVED';

/**
 * eK Score commercial terms belong to eK Score integrators only: Initiative Based integrators (open or
 * closed) approved on the eK SCORE Initiative, found by name with case and spaces ignored, as the
 * backend does.
 */
const isEkScoreIntegrator = (integrator, initiatives) =>
  integratorTypeCode(integrator?.integratorType) === 'INITIATIVE_BASED'
  && initiatives.some((item) => isEkScoreInitiative(item) && isApprovedMembership(item));

/** The integrator's Initiatives, each still waiting for approval marked "Pending approval". */
const initiativeList = (initiatives) => {
  if (initiatives.length === 0) return '-';
  return (
    <Box component="span" sx={{ display: 'inline-flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 1, rowGap: 0.5 }}>
      {initiatives.map((item, index) => (
        <Box component="span" key={item.id ?? item.name ?? index} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
          {item.name}
          {item.membershipStatus === 'PENDING' && (
            <Chip label="Pending approval" size="small" color="warning" variant="outlined" sx={{ height: 22, fontSize: 12 }} />
          )}
          {index < initiatives.length - 1 ? ',' : ''}
        </Box>
      ))}
    </Box>
  );
};

const EcosystemPartnersView = () => {

  const { id } = useParams();
  const navigate = useNavigate();
  const [ecosystemData, setEcosystemData] = useState({});
  const [subUsers, setSubUsers] = useState([]);
  const [initiatives, setInitiatives] = useState([]);
  const [loading, setLoading] = useState(false);

  const getEcosystemData = useCallback(async () => {
    try {
      const data = await ecosytemIntegatorGetById(id);
      setEcosystemData(data);
    } catch (error) {
    };
  }, [id]);

  const getSubUsers = useCallback(async () => {
    try {
      const subs = await getIntegratorSubUsers(id);
      setSubUsers(Array.isArray(subs) ? subs : []);
    } catch (error) {
      setSubUsers([]);
    }
  }, [id]);

  const getInitiatives = useCallback(async () => {
    const list = await getIntegratorInitiatives(id);
    setInitiatives(Array.isArray(list) ? list : []);
  }, [id]);

  useEffect(() => {
    getEcosystemData();
    getSubUsers();
    getInitiatives();
  }, [getEcosystemData, getSubUsers, getInitiatives]);

  return (
    <Box sx={{ width: '100%', maxWidth: { xs: '100%', lg: 1360, xl: 1280 }, boxSizing: 'border-box', mx: 'auto', px: { xs: 0, lg: 3, xl: 4 } }}>
      <Grid container alignItems="center" mb={4} rowSpacing={2} columnSpacing={2}>
        <Grid size={{ xs: 12, md: 3 }} textAlign={{ xs: 'left', md: 'left' }}>
          <Button
            onClick={() => navigate('/ecosystemPartners')}
            startIcon={<span className="material-symbols-rounded">arrow_back_ios_new</span>}
            sx={{
              minWidth: 'auto',
              px: 0,
              color: '#1C1B1F',
              textTransform: 'none',
              fontSize: 16,
              lineHeight: '24px',
              letterSpacing: '0.15px',
              fontWeight: 500,
              '& .material-symbols-rounded': { fontSize: 18 },
            }}
          >
            Back
          </Button>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }} order={{ xs: 3, md: 2 }}>
          <Typography
            className="app-page-title"
            sx={{
              textAlign: 'center',
              fontSize: { xs: 28, sm: 30, md: 34 },
              lineHeight: { xs: '34px', sm: '36px', md: '40px' },
              letterSpacing: '-0.25px',
              fontWeight: 700,
              color: '#1C1B1F',
            }}
          >
            Ecosystem Integrator Details
          </Typography>
        </Grid>

        <Grid size={{ xs: 12, md: 3 }} order={{ xs: 2, md: 3 }} textAlign={{ xs: 'left', md: 'right' }}>
          <Button
            onClick={() => { setLoading(true); navigate(`/ecosystemPartnersCreation/${id}?type=edit`) }}
            startIcon={<span className="material-symbols-rounded">edit</span>}
            sx={{
              minWidth: { xs: '100%', sm: 86 },
              height: 40,
              borderRadius: '12px',
              backgroundColor: '#DFE5F6',
              color: '#294190',
              textTransform: 'none',
              fontSize: 16,
              lineHeight: '24px',
              letterSpacing: '0.15px',
              fontWeight: 500,
              '&:hover': { backgroundColor: '#DFE5F6' },
              '& .material-symbols-rounded': { fontSize: 18 },
            }}
          >
            Edit
          </Button>
        </Grid>
      </Grid>

      <Box sx={pageCardSx}>
        <Typography sx={detailSectionTitleSx}>Integrator Details</Typography>
        <Grid container spacing={2.25}>
          <DetailField label="Ecosystem Integrator" value={ecosystemData?.integratorName} />
          <DetailField label="Country" value={ecosystemData?.countryName} />
          <DetailField label="Integrator Type" value={ecosystemData?.integratorType} transformValue={toSentenceCase} />
          <DetailField label="Email" value={ecosystemData?.email} />
          <DetailField label="Phone Number" value={[ecosystemData?.countryCode, ecosystemData?.phoneNumber].filter(Boolean).join('')} />
          <DetailField label="License" value={ecosystemData?.licenseName} />
          <DetailField
            label="Partnership Ecosystem"
            value={ecosystemData?.isOpen ? 'Open ecosystem' : 'Closed ecosystem'}
          />
          {(() => {
            const typeCode = integratorTypeCode(ecosystemData?.integratorType);
            return (
              <>
                {typeCode === 'INITIATIVE_BASED' && (
                  <DetailField label="Initiative" value={initiativeList(initiatives)} fullWidth />
                )}
                {(typeCode === 'INITIATIVE_BASED' || typeCode === 'FINANCE_BASED') && (
                  <DetailField label="eK Link login Redirect URL" value={ecosystemData?.appUrl} fullWidth />
                )}
              </>
            );
          })()}
        </Grid>
      </Box>

      <Box sx={{ ...pageCardSx, mt: 3 }}>
        <Typography sx={detailSectionTitleSx}>Sub-Users ({subUsers.length})</Typography>
        {subUsers.length > 0 ? (
          <TableContainer sx={{ border: '1px solid #DFE5F6', borderRadius: '16px' }}>
            <Table size="small" sx={{ '& td, & th': { borderColor: '#EEF1FA' } }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#F4F6FC' }}>
                  {['Name', 'Country', 'Email', 'Phone Number', 'Status'].map((head) => (
                    <TableCell key={head} sx={subUserHeadCellSx}>{head}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {subUsers.map((subUser, index) => (
                  <TableRow key={subUser?.id || index} hover>
                    <TableCell sx={subUserCellSx}>{subUser?.integratorName || '-'}</TableCell>
                    <TableCell sx={subUserCellSx}>{subUser?.countryName || '-'}</TableCell>
                    <TableCell sx={subUserCellSx}>{subUser?.email || '-'}</TableCell>
                    <TableCell sx={subUserCellSx}>{[subUser?.countryCode, subUser?.phoneNumber].filter(Boolean).join('') || '-'}</TableCell>
                    <TableCell sx={subUserCellSx}>{toSentenceCase(subUser?.status)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography sx={detailValueSx}>No sub-users found.</Typography>
        )}
      </Box>

      {ecosystemData?.ecosystemId && isEkScoreIntegrator(ecosystemData, initiatives) && (
        <EkScoreCommercialTerms ecosystemId={ecosystemData.ecosystemId} />
      )}

      {loading && <Loader />}
    </Box>
  );
};

export default EcosystemPartnersView;


